/// <amd-module name="TFS/Admin/Groups/Pivots/Settings/Components/SettingsAdminsCard" />
import * as React from "react";
import * as Resources from "TFS/Admin/Groups/Pivots/Settings/Resources";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { TeamMember } from "VSS/Platform/Contracts/WebApi";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IEntityType, TeamsMinAdminNumber } from "TFS/Admin/Groups/Common/Constants";
import { TeamAdmin } from "TFS/Admin/Groups/Common/Contracts"
import { IVssPageContext } from "VSS/Platform/Context";
import { IIdentity } from "VSSUI/IdentityPicker";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { MembershipAddPanelProps } from "TFS/Admin/Groups/Dialogs/MembershipAddPanel/MembershipAddPanel.Props";
import { FormItem } from "VSSUI/FormItem";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";

interface SettingsAdminsCardProps {
    disabled: boolean;
    teamAdmins: TeamAdmin[];
    removeAdmin: (admin: TeamMember) => void;
    showAddAdminPanel: boolean;
    pageContext: IVssPageContext;
    logTelemetry: (component: GroupsComponent, action: string, properties?: { [key: string]: any }) => void;
    onSaveMembers: (subjectIdentities: IIdentity[], adminAsMember: boolean) => void;
}

interface SettingsAdminsCardState {}

export class SettingsAdminsCard extends React.Component<SettingsAdminsCardProps, SettingsAdminsCardState> {
    private invalidMembers = new ObservableArray<IIdentity>([]);
    private validMembers = new ObservableArray<IIdentity>([]);
    private isPanelOpen = new ObservableValue<boolean>(false);

    public componentDidUpdate() {
        this.isPanelOpen.value = this.props.showAddAdminPanel;
    }

    public render(): JSX.Element {
        const { teamAdmins = [] } = this.props;
        return (
            <>
                <Card titleProps={{ text: Resources.AdministratorsSettingsCardTitle, ariaLevel: 2 }}>
                    <div className="flex-column flex-grow rhythm-vertical-16">
                        <FormItem className="group-add-item" label={Resources.AdministratorsSettingsCardText}>
                            <PillGroup className="flex-row" overflow={PillGroupOverflow.wrap}>
                                {teamAdmins.map((admin, index) => {
                                        let pill = <Pill
                                            variant={PillVariant.outlined}
                                            size={PillSize.large}
                                            key={index}
                                            onRemoveClick={
                                                teamAdmins.length > TeamsMinAdminNumber && !this.props.disabled
                                                    ? () => {
                                                          this.props.removeAdmin(admin);
                                                      }
                                                    : undefined
                                            }
                                            onRenderFilledVisual={() => {
                                                return this.renderPersona(admin);
                                            }}
                                        >
                                            {admin.identity.displayName}
                                            {!admin.isMember && <Status className="margin-left-4" {...Statuses.Warning} key="warning" size={StatusSize.s}/>}
                                        </Pill>
                                        return admin.isMember ? pill : <Tooltip text={Resources.NonMemberTooltip}>{pill}</Tooltip>;
                                })
                            }
                            </PillGroup>
                        </FormItem>
                        <div className="flex-grow">
                            <Button
                                text={Resources.AddAdminButton}
                                onClick={() => {
                                    this.invalidMembers.value = [];
                                    this.validMembers.value = [];
                                    this.isPanelOpen.value = true;
                                }}
                                disabled={this.props.disabled}
                            />
                        </div>
                    </div>
                </Card>
                <Observer isPanelOpen={this.isPanelOpen} validMembers={this.validMembers} invalidMembers={this.invalidMembers}>
                    {(props: { isPanelOpen: boolean; validMembers: IIdentity[]; invalidMembers: IIdentity[] }) => {
                        return props.isPanelOpen ? (
                            <WrappedComponent<MembershipAddPanelProps>
                                wrappedType="ms.vss-admin-web.orggroups-membership-add-panel"
                                dependencies={["ms.vss-admin-web.admin-dialogs-groups-content"]}
                                wrappedProps={{
                                    panelTitle: Resources.AddAdminPanelTitle,
                                    onDismiss: this._onDismiss,
                                    pageContext: this.props.pageContext,
                                    onSave: this.props.onSaveMembers,
                                    description: "",
                                    identityTypes: [IEntityType.Group, IEntityType.User],
                                    peoplePickerLabel: Resources.AddAdminPanelText,
                                    peoplePickerPlaceholder: Resources.AddAdminPanelText,
                                    onLogTelemetry: this.props.logTelemetry,
                                    checkMembershipExistence: this.checkMembershipExistence,
                                    removeMembers: this.removeMembers,
                                    invalidMembers: props.invalidMembers,
                                    validMembers: props.validMembers,
                                    isAdminsPage: true,
                                    permissions: {}
                                }}
                            />
                        ) : null;
                    }}
                </Observer>
            </>
        );
    }

    private _onDismiss = () => {
        this.isPanelOpen.value = false;
    };

    // For admins, the user to be added can be a member so instead, we check that the user is not alredy an admin.
    private checkMembershipExistence = (member: IIdentity) => {
        const admin = this.props.teamAdmins.find(x => x.identity.descriptor === member.subjectDescriptor);

        // if the user is already an admin, we show an error message
        if (admin) {
            this.validMembers.push(member);
            this.invalidMembers.push(member);
        } else {
            this.validMembers.push(member);
        }
    };

    private removeMembers = (members: IIdentity[]) => {
        this.validMembers.removeAll(entity => members.filter(item => item.entityId === entity.entityId).length > 0);
        this.invalidMembers.removeAll(entity => members.filter(item => item.entityId === entity.entityId).length > 0);
    };

    private renderPersona = (admin: TeamMember) => {
        return <Persona size={PersonaSize.size24} identity={admin.identity} />;
    };
}
